import React, { useState, useEffect, useRef } from 'react';
import "./OverviewPage.css";
import Dropdown from '../../shared/forms/Dropdown';
import OverviewTrafficStatCard from './OverviewTrafficStatCard';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useSites } from '../../contexts/SitesContext';
import OverviewSiteCard from './OverviewSiteCard';
import SiteListRow from './SiteListRow';
import { generatePeriodOptions } from '../../shared/lib/dateUtils';
import { percentageIncrease } from '../../shared/lib/numberUtils';
import Separator from '../../shared/components/Separator';
import { useMobile } from '../../contexts/MobileContext';
import { ReactComponent as GridIcon } from '../../assets/icons/grid.svg';
import { ReactComponent as ListIcon } from '../../assets/icons/list.svg';
import { gridTemplateOverview, ListHeader, useViewport } from '../../shared/components/tableUtils';

const OverviewPage = ({position, setPosition}) => {
  const [displayMode, setDisplayMode] = useState(null);
  const { getSites, reorderSites } = useSites();
  const [totalTraffic, setTotalTraffic] = useState(null);
  const { isMobile } = useMobile();
  const { vw } = useViewport();

  const handleDragEnd = (result) => {
    const { source, destination } = result;
    reorderSites(source, destination);
  };

  const aggregateSites = (sites) => {
    let result = { total: {numClicks: 0,
                           percentage: null,
                           old: null,
                           title: 'TOTAL TRAFFIC',
                           icon: 'click'
                          },
                   bot: {numClicks: 0,
                         clickPercentage: 0,
                         percentage: null,
                         old: null,
                         title: 'BOT TRAFFIC',
                         icon: 'bot'
                        }
                  };
    sites.forEach(site => {
      result.total.numClicks += site.humanClicks + site.botClicks;
      result.bot.numClicks += site.botClicks;

      if (site.previousHumanClicks !== null) {
        result.total.old += site.previousHumanClicks + site.previousBotClicks;
        result.bot.old += site.previousBotClicks;
      }
    });

    if (result.total.old !== null && result.total.old !== 0) {
      result.total.percentage = percentageIncrease(result.total.old, result.total.numClicks);

      if (result.bot.old !== null && result.bot.old !== 0) {
        let oldBotPercentage = result.bot.old / result.total.old;
        let newBotPercentage = result.bot.numClicks / result.total.numClicks;
        result.bot.percentage = (newBotPercentage - oldBotPercentage) * 100;
      }
    }

    if (result.total.numClicks !== 0) {
      result.bot.clickPercentage = result.bot.numClicks / result.total.numClicks * 100;
    }

    return result;
  };

  useEffect(() => {
    const dm = localStorage.getItem('displayMode') || 'list';
    setDisplayMode(dm);
    setTotalTraffic(aggregateSites(getSites()));
  }, [getSites]);

  const setDisplay = (display) => {
    localStorage.setItem('displayMode', display);
    setDisplayMode(display);
  };

  const ref = useRef(null);
  const sites = getSites();
  const showOverviewReorder = !isMobile;
  const headerGrid = gridTemplateOverview(isMobile, vw, showOverviewReorder);
  const metricsVisible = !(isMobile || vw <= 980);

  return (
    <div className="page-shell overview"
         ref={ref}
         onLoad={() => ref.current.scrollTop = position }
         onScroll={() => setPosition(ref.current.scrollTop)}>

      <div className="dashboard-filter-bar">
        <Dropdown
          id="period"
          title="Period"
          options={generatePeriodOptions()}
        />
      </div>

      <h2 className="page-header">Account Overview</h2>

      <div className="page-content-shell">
        {totalTraffic && (
          <div className="traffic-cards">
            <OverviewTrafficStatCard
              title='Total Traffic'
              icon='click'
              numClicks={totalTraffic.total.numClicks}
              percentage={totalTraffic.total.percentage}/>

            <OverviewTrafficStatCard
              title='Bot Traffic'
              icon='bot'
              numClicks={totalTraffic.bot.numClicks}
              clickPercentage={totalTraffic.bot.clickPercentage}
              percentage={totalTraffic.bot.percentage}/>
          </div>
        )}

        <div className="section-header__with-buttons">
          <h4 className="section-title section-title__with-count">
            Websites
            <span className="section-count">{sites.length}</span>
          </h4>
          <div className="icon-row">
            <ListIcon className={`icon sm ${displayMode === 'list' ? 'active' : ''}`}
                      onClick={() => setDisplay('list')} />
            <GridIcon className={`icon sm ${displayMode === 'grid' ? 'active' : ''}`}
                      onClick={() => setDisplay('grid')} />
          </div>
        </div>

        <section
          className={`${
            displayMode === 'grid' ? 'section-box--tight' : 'section-box--pad'
          } overview-websites`}
        >
        {displayMode === 'list' && (
          <ListHeader
            columns={headerGrid}
            cells={
              showOverviewReorder
                ? [
                    null,
                    null,
                    'URL',
                    null,
                    (
                      <>
                        Fraud{' '}
                        <span className="hide-mobile">
                          Level
                        </span>
                      </>
                    ),
                    metricsVisible ? 'Bot Traffic' : '',
                    'Status',
                  ]
                : [
                    null,
                    'URL',
                    null,
                    (
                      <>
                        Fraud{' '}
                        <span className="hide-mobile">
                          Level
                        </span>
                      </>
                    ),
                    metricsVisible ? 'Bot Traffic' : '',
                    'Status',
                  ]
            }
          />
        )}

        {displayMode === 'list' && (
          <Separator className="section-divider section-divider--tight" />
        )}

        {sites.length === 0 && (
          <span className="empty-state">
            No websites
          </span>
        )}

        {displayMode === 'list' && (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="droppable" direction="vertical">
              {(provided) => (
                <div {...provided.droppableProps}
                  ref={provided.innerRef}>
                  {sites.map((site, index) => (
                    <div key={site.website} className="sitecard-container">
                      <Draggable draggableId={site.website}
                                 index={index}>
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}>

                            <SiteListRow site={site}
                                         index={index}
                                         dragHandleProps={provided.dragHandleProps} />
                            <Separator />
                          </div>
                        )}
                      </Draggable>
                    </div>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}

        {displayMode === 'grid' && (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="droppable" direction="vertical">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className='website-grid'>
                  {sites.map((site, index) => (
                    <div key={site.website}>
                      <Draggable draggableId={site.website}
                                index={index}>
                        {(provided) => (
                          <div className="overview-grid-item"
                               ref={provided.innerRef}
                               {...provided.draggableProps}>
                            <OverviewSiteCard site={site}
                                              dragHandleProps={provided.dragHandleProps} />
                          </div>
                        )}
                      </Draggable>
                    </div>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
        </section>
      </div>
    </div>
  );
};

export default OverviewPage;
